/**
 *
 * Participant's Screen Name
 */
import { Box, Button, Checkbox, FormControlLabel, Modal, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import useAuth from 'hooks/useAuth';
import useUser from 'hooks/useUser';
import { RefObject, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMutation } from 'react-query';
import { useParams } from 'react-router';
import { useSearchParam } from 'react-use';
import ConnectionTest from './ConnectionTest';

interface Props {
  initialized: boolean;
  containerRef: RefObject<HTMLDivElement> | null;
  publisherRef: any;
  sessionRef: any;
  sessionStarted: boolean;
  setSessionStarted: Function;
  screenName: string;
  setScreenName: Function;
  publishVideo: boolean;
  publishAudio: boolean;
  setPublishVideo: Function;
  setPublishAudio: Function;
  floatingEmail: string | null | undefined;
  isCameraAvailable: Function;
  arrivedRef: any;
}

const ScreenName = ({
  initialized,
  containerRef,
  publisherRef,
  sessionRef,
  sessionStarted,
  setSessionStarted,
  screenName,
  setScreenName,
  publishVideo,
  publishAudio,
  setPublishVideo,
  setPublishAudio,
  floatingEmail,
  isCameraAvailable,
  arrivedRef
}: Props) => {
  const intl = useIntl();
  const classes = useStyles();
  const [showConnectionTest, setShowConnectionTest] = useState(false);

  const { userId, request } = useAuth();
  const { user: loggedUser } = useUser(userId);
  const { sessionId } = useParams();
  const challenge = useSearchParam('challenge');

  const updateLoggedUserScreenName = useMutation((screenName: string) =>
    request.put(`/session/${sessionId}/participant/${userId}/`, { screenName })
  );

  const updateFloatingUserScreenName = useMutation((screenName: string) =>
    request.put(`/session/${sessionId}/floating/${challenge}/`, { screenName })
  );

  useEffect(() => {
    // Get previous settings from localStorage
    setScreenName(localStorage.getItem('screenName') ?? '');
    setPublishVideo(localStorage.getItem('publishVideo') === 'true' || !localStorage.getItem('publishVideo'));
    setPublishAudio(localStorage.getItem('publishAudio') === 'true' || !localStorage.getItem('publishAudio'));
  }, [sessionId, setPublishAudio, setPublishVideo, setScreenName, setSessionStarted]);

  const handleError = (error) => {
    if (error) {
      console.error('...handleError', error);
    }
  };

  const joinSession = () => {
    if (!sessionRef.current) return;

    let finalScreenName = screenName;
    if (!finalScreenName) {
      if (loggedUser?.firstName) {
        finalScreenName = `${loggedUser?.firstName} ${loggedUser?.lastName}`;
      }
      if (floatingEmail) {
        finalScreenName = floatingEmail?.match(/^([^@]*)@/)![1] ?? '';
      }
      setScreenName(finalScreenName);
    }

    if (floatingEmail) {
      // Floating session
      updateFloatingUserScreenName.mutate(finalScreenName);
    } else {
      // Normal session
      updateLoggedUserScreenName.mutate(finalScreenName);
    }

    localStorage.setItem('sessionId', sessionId ?? '');
    localStorage.setItem('screenName', finalScreenName);

    // Publish
    sessionRef.current.publish(publisherRef.current, handleError);

    if (arrivedRef.current) {
      publisherRef.current.publishVideo(publishVideo);
      publisherRef.current.publishAudio(publishAudio);
    }

    setSessionStarted(true);
  };

  return (
    <Modal
      container={containerRef?.current}
      open={!sessionStarted}
      onClose={() => {
        setSessionStarted(true);
      }}
    >
      <div className={classes.root}>
        <div className="container">
          <div className="box-border">
            <div className="Input">
              <TextField
                label={intl.formatMessage({ defaultMessage: 'Please enter your screen name' })}
                onChange={(e) => setScreenName(e.target.value)}
                value={screenName}
                fullWidth
              />
            </div>

            <Box style={{ display: 'flex', marginTop: 10, marginBottom: 20 }}>
              <div className="col-md-6 d-flex w-100 p-0" style={{ alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={publishAudio}
                      onChange={(e) => {
                        setPublishAudio(e.target.checked);
                        localStorage.setItem('publishAudio', String(e.target.checked));
                      }}
                      name="joinWithAudio"
                    />
                  }
                  label={intl.formatMessage({ defaultMessage: 'Join with Audio' })}
                />
              </div>
              <div className="col-md-6 d-flex w-100 p-0" style={{ alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={publishVideo}
                      onChange={async (e) => {
                        setPublishVideo(e.target.checked);
                        localStorage.setItem('publishVideo', String(e.target.checked));
                      }}
                      name="joinWithVideo"
                    />
                  }
                  label={intl.formatMessage({ defaultMessage: 'Join with Video' })}
                />
              </div>
            </Box>

            <Button variant="contained" type="submit" onClick={joinSession} fullWidth disabled={!initialized}>
              <FormattedMessage defaultMessage={' Join Session'} />
            </Button>

            {!showConnectionTest ? (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 15 }}>
                <Button
                  type="button"
                  onClick={() => {
                    setShowConnectionTest(true);
                  }}
                  disabled={!initialized}
                >
                  <FormattedMessage defaultMessage={'   Test your internet connection'} />
                </Button>
              </div>
            ) : (
              <ConnectionTest />
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ScreenName;

const useStyles = makeStyles({
  root: {
    width: '100vw',
    height: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',

    '& .container': {
      border: '1px solid #ddd',
      backgroundColor: '#fff',
      padding: '60px 30px 30px 30px',
      minWidth: '35%',
      borderRadius: 4
    }
  }
});
